// Shared chrome for chart-family visuals: title bar, padding, card styling,
// and an empty/error placeholder. Pure presentation — no ECharts dep here.
//
// P1-tooltip: ChartShell also exposes optional hover-tooltip-page handlers so
// that the chart shell div can drive a TooltipPagePopover anchored to the
// cursor. To avoid touching the 14 chart-visual files (Agent A2 territory),
// the page-level wrapper (PageView.tsx :: VisualOnCanvas) is the primary
// hover trigger — it has access to spec.tooltipPageFolder directly. This
// hook here is a documented seam for future direct wiring from any chart
// visual that wants to bind hover on its inner card region instead of the
// outer position-wrapper.

import { useEffect, useRef, useState, type ReactNode } from "react";
import "./_chartShell.css";
// AGENT-R: drill-button styling (separate file so we don't modify _chartShell.css).
import "./_drillButton.css";
import { TooltipPagePopover } from "./TooltipPagePopover";
// AGENT-R: drill drawer store — clicking the "..." button opens the right-
// anchored drawer with this visual's raw rows.
import { useDrillStore } from "../state/drillStore";

interface ChartShellProps {
  title?: string | null;
  status?: string | null;
  isEmpty?: boolean;
  emptyText?: string;
  children: ReactNode;
  // P1-tooltip: when a chart-family visual sets `tooltipPageFolder` here,
  // ChartShell binds onMouseEnter/onMouseLeave on its inner card div and
  // renders a TooltipPagePopover after a 300ms debounce. Optional and
  // backwards-compatible — existing call-sites that omit it get unchanged
  // behavior.
  tooltipPageFolder?: string | null;
  // AGENT-R: when provided, render the small "..." drill button in the
  // title bar's top-right corner. Clicking opens the DataDrillDrawer
  // anchored to this visual id. Backwards-compatible — omitting the prop
  // hides the button entirely.
  visualId?: string | null;
}

// P1-tooltip: hover debounce — matches PBI's "open after 300ms" feel.
const HOVER_DEBOUNCE_MS = 300;

export function ChartShell({
  title,
  status,
  isEmpty,
  emptyText = "No data",
  children,
  tooltipPageFolder,
  visualId, // AGENT-R: when set, the "..." drill button is rendered.
}: ChartShellProps) {
  // AGENT-R: stable handler — read the store on click instead of subscribing
  // so the shell doesn't re-render when drawer state mutates elsewhere.
  const openDrill = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (visualId) useDrillStore.getState().openVisual(visualId);
  };
  // P1-tooltip: hover state for the popover. Only engages when
  // tooltipPageFolder is truthy — i.e., the visual is bound to a canvas
  // tooltip page.
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [anchor, setAnchor] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const debounceTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    };
  }, []);

  const hoverable = !!tooltipPageFolder;

  // P1-tooltip: handlers — only attached when hoverable so non-tooltip visuals
  // have zero overhead.
  const onEnter = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchor({ x: e.clientX, y: e.clientY });
    if (debounceTimer.current) window.clearTimeout(debounceTimer.current);
    debounceTimer.current = window.setTimeout(() => {
      setPopoverVisible(true);
    }, HOVER_DEBOUNCE_MS);
  };
  const onMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchor({ x: e.clientX, y: e.clientY });
  };
  const onLeave = () => {
    if (debounceTimer.current) {
      window.clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    setPopoverVisible(false);
  };

  return (
    <div
      className="vshell"
      onMouseEnter={hoverable ? onEnter : undefined}
      onMouseMove={hoverable ? onMove : undefined}
      onMouseLeave={hoverable ? onLeave : undefined}
    >
      {title && (
        <div className="vshell-title" title={title}>
          <span className="vshell-title-text">{title}</span>
          {/* AGENT-R: drill-button — only rendered when caller passes visualId. */}
          {visualId ? (
            <button
              type="button"
              className="vshell-drill-btn"
              onClick={openDrill}
              aria-label="Show underlying rows"
              title="Show underlying rows"
            >
              ⋯
            </button>
          ) : null}
        </div>
      )}
      <div className="vshell-body">
        {isEmpty || status === "empty" ? (
          <div className="vshell-empty">{emptyText}</div>
        ) : status === "error" ? (
          <div className="vshell-error">Data error</div>
        ) : (
          children
        )}
      </div>
      {hoverable && tooltipPageFolder ? (
        <TooltipPagePopover
          folder={tooltipPageFolder}
          anchor={anchor}
          visible={popoverVisible}
        />
      ) : null}
    </div>
  );
}
